import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { loadAll } from "../lib/storage";
import { ESTADOS } from "../lib/config";
import { exportAlbaranesExcel } from "../lib/excel";
import { EstadoBadge } from "../components/EstadoBadge";
import { Plus, Search, Inbox, FileSpreadsheet, ChevronRight, ArrowRight, MapPin } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const fullName = (p) => `${p?.nombre || ""} ${p?.apellidos || ""}`.trim() || "—";

const FILTERS = [{ key: "todos", label: "Todos" }, ...ESTADOS.map((e) => ({ key: e.key, label: e.label }))];

export default function AlbaranList() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("todos");

  useEffect(() => {
    setItems(loadAll().sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || "")));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((a) => {
      if (filter !== "todos" && a.estado !== filter) return false;
      if (!q) return true;
      const hay = [
        a.numero,
        fullName(a.remitente),
        fullName(a.destinatario),
        a.remitente?.dni,
        a.destinatario?.dni,
        a.remitente?.direccion,
        a.destinatario?.direccion,
      ];
      return hay.filter(Boolean).some((v) => v.toLowerCase().includes(q));
    });
  }, [items, query, filter]);

  const handleExport = () => {
    if (filtered.length === 0) {
      toast.error("No hay albaranes para exportar.");
      return;
    }
    exportAlbaranesExcel(filtered);
    toast.success(`Exportados ${filtered.length} albaranes a Excel`);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 md:py-10">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900">Albaranes</h2>
          <p className="text-sm text-slate-500 mt-1">Guardados en este dispositivo. Búscalos, ábrelos, edítalos o expórtalos.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            data-testid="export-excel-btn"
            className="inline-flex items-center justify-center gap-1.5 h-12 px-4 rounded-lg text-sm font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition-colors"
          >
            <FileSpreadsheet className="h-4 w-4" /> Excel
          </button>
          <Link
            to="/nuevo"
            data-testid="create-note-btn"
            className="inline-flex items-center justify-center gap-1.5 h-12 px-5 rounded-lg text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" /> Nuevo albarán
          </Link>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por número, nombre, DNI o dirección..."
            data-testid="search-input"
            className="w-full h-11 pl-10 pr-3 text-base border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
          />
        </div>
      </div>

      <div className="flex items-center gap-2 mb-5 overflow-x-auto pb-1" data-testid="filter-bar">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`h-9 px-4 shrink-0 rounded-full text-sm font-semibold transition-colors ${
              filter === f.key ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div
          data-testid="empty-state"
          className="flex flex-col items-center justify-center text-center py-16 bg-white border border-dashed border-slate-300 rounded-xl"
        >
          <Inbox className="h-10 w-10 text-slate-300 mb-3" />
          <p className="font-semibold text-slate-700">
            {query || filter !== "todos" ? "Sin resultados" : "No hay albaranes todavía"}
          </p>
          <p className="text-sm text-slate-500 mt-1 mb-4">
            {query || filter !== "todos" ? "Prueba con otra búsqueda o filtro." : "Crea tu primer albarán para empezar."}
          </p>
          {!query && filter === "todos" && (
            <Link to="/nuevo" className="inline-flex items-center gap-1.5 h-11 px-5 rounded-lg text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 transition-colors">
              <Plus className="h-4 w-4" /> Crear albarán
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-3" data-testid="albaranes-list">
          {filtered.map((a) => (
            <Link
              key={a.id}
              to={`/albaran/${a.id}`}
              data-testid={`albaran-row-${a.id}`}
              className="group block bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md hover:border-slate-300 transition-all"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs font-bold text-slate-400">{a.numero}</span>
                  <EstadoBadge estado={a.estado} />
                </div>
                <span className="text-xs text-slate-500">{fmtDate(a.createdAt)}</span>
              </div>
              <div className="mt-2 flex items-center gap-2 text-slate-900 font-semibold flex-wrap">
                <span>{fullName(a.remitente)}</span>
                <ArrowRight className="h-4 w-4 text-slate-400" />
                <span>{fullName(a.destinatario)}</span>
                <ChevronRight className="h-5 w-5 text-slate-300 ml-auto group-hover:text-slate-500 transition-colors" />
              </div>
              <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-500">
                <span className="flex items-center gap-1.5 truncate"><MapPin className="h-3 w-3 text-blue-500 shrink-0" /> Recogida: {a.remitente?.direccion || "—"}</span>
                <span className="flex items-center gap-1.5 truncate"><MapPin className="h-3 w-3 text-violet-500 shrink-0" /> Entrega: {a.destinatario?.direccion || "—"}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
